import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { NotificationService } from '@shared/services/notification.service';
import { SessionTrackingService } from '@core/services/session-tracking.service';
import { MessageService } from 'primeng/api';
import { filter, tap } from 'rxjs/operators';
import { ToastModule } from 'primeng/toast';

import { RouterOutlet } from '@angular/router';
import { UserStore } from '@store/user/user.store';
import { LocationStore } from '@store/location/location.store';
import { BuildingStore } from '@store/building/building.store';
import { FloorStore } from '@store/floor/floor.store';
import { RegionStore } from '@store/region/region.store';
import { FloorPlanStore } from '@store/floor-plan/floor-plan.store';
import { AccessPointStore } from '@store/access-point/access-point.store';
import { ReportStore } from '@store/report/report.store';
import { DepartmentStore } from '@store/department/department.store';
import { RoleStore } from '@store/role/role.store';
import { ZoneStore } from '@store/zone/zone.store';
import { SystemStore } from '@store/system/system.store';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-root',
    template: `@if (!isIframe) {<router-outlet />} <p-toast position="bottom-right" />`,
    imports: [ToastModule, RouterOutlet],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MessageService],
})
export class AppComponent implements OnInit {
    title = 'airia';
    isIframe = false;
    private initialDataLoaded = false;
    private selfRequestStarted = false;

    private readonly userStore = inject(UserStore);
    private readonly locationStore = inject(LocationStore);
    private readonly regionStore = inject(RegionStore);
    private readonly zoneStore = inject(ZoneStore);
    private readonly buildingStore = inject(BuildingStore);
    private readonly floorStore = inject(FloorStore);
    private readonly floorPlanStore = inject(FloorPlanStore);
    private readonly accessPointStore = inject(AccessPointStore);
    private readonly reportStore = inject(ReportStore);
    private readonly systemStore = inject(SystemStore);
    private readonly departmentStore = inject(DepartmentStore);
    private readonly roleStore = inject(RoleStore);
    private readonly notificationService = inject(NotificationService);
    private readonly messageService = inject(MessageService);
    private readonly sessionTracking = inject(SessionTrackingService);
    private readonly authService = inject(MsalService);
    private readonly msalBroadcastService = inject(MsalBroadcastService);
    private readonly destroyRef = inject(DestroyRef);

    errorMessage$ = this.notificationService.errorMessage$
        .pipe(
            tap((errorMessage) => {
                if (errorMessage !== null) {
                    this.displayErrorMessage(errorMessage);
                }
            })
        )
        .subscribe();
    successMessage$ = this.notificationService.successMessage$
        .pipe(
            tap((successMessage) => {
                if (successMessage !== null) {
                    this.displaySuccessMessage(successMessage);
                }
            })
        )
        .subscribe();

    constructor() {
        effect(() => {
            if (this.userStore.self() && !this.initialDataLoaded) {
                this.initialDataLoaded = true;
                // Load initial data only if user self is available
                this.userStore.getAll();
                this.locationStore.getAll();
                this.regionStore.getAll();
                this.systemStore.getAll();
                if (environment.zonesEnabled) this.zoneStore.getAll();
                this.buildingStore.getAll({ forceReload: true });
                this.buildingStore.getClassifications();
                this.floorStore.getAll();
                this.floorPlanStore.getAll();
                this.accessPointStore.getAll();
                this.departmentStore.getAll();
                this.roleStore.getAll();
                const html = document.querySelector('html');
                if (html) {
                    if (this.userStore.self()?.userPreferences?.userSettings?.theme === 'light') {
                        html.classList.value = 'theme-light';
                    } else {
                        html.classList.value = 'theme-dark';
                    }
                }
            }
        });
    }

    ngOnInit(): void {
        this.isIframe = window !== window.parent && !window.opener;

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_START),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => this.sessionTracking.endSession('logout'));

        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                const accounts = this.authService.instance.getAllAccounts();
                if (!this.authService.instance.getActiveAccount() && accounts.length === 1) {
                    this.authService.instance.setActiveAccount(accounts[0]);
                }
                const activeAccount = this.authService.instance.getActiveAccount();
                if (activeAccount && !this.selfRequestStarted) {
                    this.selfRequestStarted = true;
                    this.userStore.getSelf();
                }
                if (activeAccount && !this.sessionTracking.active) {
                    this.sessionTracking.startSession();
                }
            });
    }

    displayError(error: HttpErrorResponse): void {
        this.addMessageToService(error.message, 'error', 'Error');
    }

    displayErrorMessage(error: string): void {
        this.addMessageToService(error, 'error', 'Error');
    }

    displaySuccessMessage(message: string): void {
        this.addMessageToService(message, 'success', 'Success');
    }

    private addMessageToService(message: string, severity: string, summary: string): void {
        this.messageService.add({
            severity,
            summary,
            detail: message,
            life: 5000,
            sticky: false,
        });
    }
}
